import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalBody,
  Flex,
  ModalHeader,
  ModalFooter,
  Box,
  VStack,
  Text,
  useMediaQuery,
  Button,
  useToast,
  Progress,
} from '@chakra-ui/react';
import Dropzone from 'react-dropzone';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Csv from 'components/Icons/Csv';
import { getSelectedDataFileDetails, postVersionFile, versionData } from 'redux/DataImportSlice';
import { importAcceptedFormat } from 'utils/FileFormats';
import CustomToast from 'components/CustomToast';
import { AppDispatch } from 'utils/GlobalHelpers';
import {
  DATA_IMPORT_SUPPORTED_LABEL,
  getDataImportUploadKind,
  uploadCsvDataImportFile,
  uploadRawDataImportFile,
} from 'utils/dataImportUpload';

const AddVersionModal = ({ setVersionModal, versionModal }: any) => {
  const dispatch: AppDispatch = useDispatch();
  const toast = useToast();
  const [isSmallLaptop] = useMediaQuery('(max-width: 1415px)');
  const [loader, setLoader] = useState(false);
  const [progress, setProgress] = useState(0);
  const selectedDataFileDetails = useSelector(getSelectedDataFileDetails);
  const dataSourceId = selectedDataFileDetails?.id;

  const handleUpload = async (file: File) => {
    if (file.size > 10 * 1024 * 1024) {
      toast({
        duration: 2500,
        render: () => <CustomToast title='File size exceeds 10MB limit.' status='error' />,
      });
      return;
    }

    const kind = getDataImportUploadKind(file);
    if (!kind) {
      toast({
        duration: 2500,
        render: () => (
          <CustomToast
            title={`Please upload a ${DATA_IMPORT_SUPPORTED_LABEL} file.`}
            status='error'
          />
        ),
      });
      return;
    }

    setLoader(true);
    try {
      const s3Url =
        kind === 'csv'
          ? await uploadCsvDataImportFile(file, 'blox-data-import', setProgress)
          : await uploadRawDataImportFile(file, 'blox-data-import', setProgress);

      const requestBody = {
        s3_file_url: s3Url,
      };

      await dispatch(
        postVersionFile({
          dataSourceId,
          request_body: requestBody,
          toaster: true,
        }),
      );
      await dispatch(versionData(dataSourceId));
      setVersionModal.off();
    } catch (error) {
      const message =
        error instanceof Error && error.message === 'EMPTY_FILE'
          ? 'The uploaded file is empty.'
          : 'Upload failed. Please try again.';
      toast({
        duration: 2500,
        render: () => <CustomToast title={message} status='error' />,
      });
    } finally {
      setLoader(false);
      setProgress(0);
    }
  };

  const handleFileDrop = (acceptedFiles: File[], rejectedFiles: any) => {
    if (rejectedFiles.length > 0) {
      toast({
        duration: 2500,
        render: () => (
          <CustomToast
            title={`Please upload a ${DATA_IMPORT_SUPPORTED_LABEL} file.`}
            status='error'
          />
        ),
      });
      return;
    }

    if (acceptedFiles[0]) {
      handleUpload(acceptedFiles[0]);
    }
  };

  const textStyles = {
    fontFamily: 'regular_roboto',
    fontWeight: '400',
  };
  return (
    <>
      <Modal
        onClose={() => setVersionModal.off()}
        isOpen={versionModal}
        isCentered
        colorScheme='black'
      >
        <ModalOverlay />
        <ModalContent color={'#000'} maxW={'50rem'} height={'25rem'}>
          <ModalCloseButton />
          <ModalHeader />
          <ModalBody>
            {loader ? (
              <Flex
                h='full'
                direction='column'
                alignItems='center'
                justifyContent='center'
                justify='center'
                align='center'
              >
                <Text fontSize={'xl'} textAlign={'center'} p={4}>
                  {progress}%
                </Text>
                <Progress width={'80%'} maxW={'50rem'} hasStripe value={progress} />
              </Flex>
            ) : (
              <Box height={'100%'} py={4} px={10}>
                <Dropzone
                  onDrop={(acceptedFiles, rejectedFiles) =>
                    handleFileDrop(acceptedFiles, rejectedFiles)
                  }
                  multiple={false}
                  accept={importAcceptedFormat}
                >
                  {({ getRootProps, getInputProps }) => (
                    <Flex
                      align={'center'}
                      height={'100%'}
                      border={'2px dashed #6562CF'}
                      borderRadius={'lg'}
                      justifyContent={'space-around'}
                      {...getRootProps({ className: 'dropzone' })}
                    >
                      <input {...getInputProps()} />
                      <VStack justifyContent={'space-around'} alignItems={'center'}>
                        <Csv innerColor='#6562CF' width='70' height='70' />
                        <Text
                          my='4'
                          color='black'
                          {...textStyles}
                          fontSize={isSmallLaptop ? 'md' : 'lg'}
                          textAlign={'center'}
                          fontWeight={'400'}
                        >
                          Drag and drop your {DATA_IMPORT_SUPPORTED_LABEL.toLowerCase()} file here{' '}
                          <br /> - OR -
                        </Text>

                        <Button
                          style={{ color: 'white', background: '#4a4889', width: 'fit-content' }}
                          minH={'50px'}
                          borderRadius={'15px'}
                          px={8}
                        >
                          <Text
                            {...textStyles}
                            fontSize={isSmallLaptop ? 'sm' : 'md'}
                            textAlign={'start'}
                            color='white'
                            textTransform='none'
                          >
                            Browse files
                          </Text>
                        </Button>
                      </VStack>
                    </Flex>
                  )}
                </Dropzone>
              </Box>
            )}
          </ModalBody>
          <ModalFooter />
        </ModalContent>
      </Modal>
    </>
  );
};

export default AddVersionModal;
